import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { navLinks, companyInfo } from '../../data/siteData';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const projectLinks = [
    { name: 'All Projects', path: '/projects' },
    { name: 'Ongoing', path: '/projects?status=Ongoing' },
    { name: 'Completed', path: '/projects?status=Completed' },
    { name: 'Upcoming', path: '/upcoming' },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-slate-100'
          : 'bg-white/80 backdrop-blur-xl'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/logo.png" alt="Modern Arch Logo" className="h-16 w-auto object-contain" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.name === 'Projects' ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger
                    className="flex items-center gap-1 text-slate-700 hover:text-[#47ace2] font-medium font-manrope transition-colors"
                    data-testid="projects-dropdown-trigger"
                  >
                    Projects
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {projectLinks.map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link
                          to={item.path}
                          className="w-full cursor-pointer"
                          data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                        >
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`font-medium font-manrope transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#47ace2]'
                      : 'text-slate-700 hover:text-[#47ace2]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* CTA & Mobile Toggle */}
          <div className="flex items-center gap-4">
            <a
              href={`tel:${companyInfo.primaryPhone || '+918766551928'}`}
              className="hidden md:flex items-center gap-2 text-slate-600 hover:text-[#47ace2] transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-manrope text-sm">{companyInfo.phone}</span>
            </a>
            <Link to="/contact">
              <Button
                data-testid="contact-us-btn"
                className="hidden md:inline-flex bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full px-6"
              >
                Contact Us
              </Button>
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden py-4 border-t border-slate-100 animate-in slide-in-from-top-2"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`px-4 py-3 rounded-lg font-medium font-manrope transition-colors ${
                    location.pathname === link.path
                      ? 'bg-[#F0F8FF] text-[#47ace2]'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/upcoming" className="px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-50 font-medium font-manrope">
                Upcoming Projects
              </Link>
              <Link to="/contact" className="mt-2">
                <Button className="w-full bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
